import { useEffect, useRef, useState } from "react";
import { Send, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, streamPost } from "@/lib/api";
import { PageHeader } from "@/components/kit";
import { Md } from "@/pages/Haccp";

const SUGGESTIONS = [
  "What should I do if the walk-in fridge reads 9°C?",
  "Which of our dishes contain gluten?",
  "What will an EHO look at during an inspection?",
  "Summarise our open incidents and what's needed to close them",
];

export default function Assistant() {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);

  useEffect(() => {
    api.get("/ai/chat").then((r) => setMsgs(r.data));
  }, []);
  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  const send = async (q) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    try {
      await streamPost("/ai/chat", { message }, (t) => setMsgs((m) => [...m.slice(0, -1), { role: "assistant", text: m[m.length - 1].text + t }]));
    } catch (e) {
      toast.error(e.message);
      setMsgs((m) => m.slice(0, -1));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    await api.delete("/ai/chat");
    setMsgs([]);
  };

  return (
    <div className="flex h-[calc(100vh-11rem)] flex-col lg:h-[calc(100vh-6rem)]">
      <PageHeader eyebrow="Uses your own records" title="Ask AI" subtitle="Food safety answers grounded in your equipment, incidents, allergens and checks."
        actions={msgs.length > 0 && <button data-testid="clear-chat-button" onClick={clear} className="ka-press inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold"><Trash2 className="h-4 w-4" />Clear</button>} />
      <div data-testid="chat-messages" className="ka-card flex-1 space-y-4 overflow-y-auto p-5">
        {!msgs.length && (
          <div className="grid gap-3 sm:grid-cols-2">
            {SUGGESTIONS.map((s, i) => <button key={s} data-testid={`chat-suggestion-${i}`} onClick={() => send(s)} className="ka-press rounded-xl border border-slate-200 p-4 text-left text-sm text-slate-700 hover:border-emerald-500 hover:bg-emerald-50/40"><Sparkles className="mb-2 h-4 w-4 text-emerald-600" />{s}</button>)}
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
            <div className={m.role === "user" ? "max-w-[85%] rounded-2xl rounded-br-sm bg-slate-950 px-4 py-2.5 text-sm text-white" : "max-w-[95%]"}>
              {m.role === "user" ? m.text : m.text ? <Md>{m.text}</Md> : <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />}
            </div>
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-3 flex gap-2">
        <input data-testid="chat-input" className="ka-input h-12" value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask about food safety, HACCP, allergens…" />
        <button data-testid="chat-send-button" disabled={busy} className="ka-press flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"><Send className="h-4 w-4" /></button>
      </form>
    </div>
  );
}
